import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import type {
  StorageAddressRef,
  InventoryTableRow,
  ReservePayload,
} from "../ProductsTable/types";

interface ReserveModalProps {
  open: boolean;
  product: InventoryTableRow;
  storageAddresses: StorageAddressRef[];
  onClose: () => void;
  /** Called with a validated ReservePayload when the user confirms */
  onReserve: (payload: ReservePayload) => Promise<void>;
}

/**
 * Modal dialog to reserve stock from a storage address.
 * Validates client-side before calling the backend:
 *  - quantity must be > 0
 *  - location must be selected
 */
export function ReserveModal({
  open,
  product,
  storageAddresses,
  onClose,
  onReserve,
}: ReserveModalProps) {
  const [addressId, setAddressId] = useState<number>(
    storageAddresses[0]?.id ?? 0
  );
  const [quantity, setQuantity] = useState<number>(0);
  const [clientReference, setClientReference] = useState("");
  const [quoteReference, setQuoteReference] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Client-side validation gates
  const isQuantityValid = Number.isInteger(quantity) && quantity > 0;
  const isLocationSelected = addressId > 0;
  const canConfirm = isQuantityValid && isLocationSelected;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setIsSubmitting(true);
    try {
      const payload: ReservePayload = {
        product_id: product.id,
        storage_address_id: addressId,
        quantity,
      };
      if (clientReference.trim()) {
        payload.client_reference = clientReference.trim();
      }
      if (quoteReference.trim()) {
        payload.quote_reference = quoteReference.trim();
      }
      await onReserve(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setQuantity(Number.isNaN(value) ? 0 : value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reservar stock — {product.name}</DialogTitle>

      <DialogContent>
        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel id="address-label">Ubicación</InputLabel>
          <Select
            labelId="address-label"
            inputProps={{ "data-testid": "address-select" }}
            value={addressId}
            label="Ubicación"
            onChange={(e) => setAddressId(e.target.value as number)}
          >
            {storageAddresses.map((addr) => (
              <MenuItem key={addr.id} value={addr.id}>
                {addr.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Cantidad"
          type="number"
          variant="outlined"
          fullWidth
          margin="normal"
          value={quantity === 0 ? "" : quantity}
          onChange={handleQuantityChange}
          inputProps={{
            min: 1,
            step: 1,
            "aria-label": "cantidad",
          }}
        />

        <TextField
          label="Referencia cliente"
          variant="outlined"
          fullWidth
          margin="normal"
          value={clientReference}
          onChange={(e) => setClientReference(e.target.value)}
          inputProps={{
            "aria-label": "referencia cliente",
          }}
        />

        <TextField
          label="Referencia cotización"
          variant="outlined"
          fullWidth
          margin="normal"
          value={quoteReference}
          onChange={(e) => setQuoteReference(e.target.value)}
          inputProps={{
            "aria-label": "referencia cotización",
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default" disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!canConfirm || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReserveModal;
